import type { EngineInput, EngineOutput } from '../engine/types';
import type { OptimizeRequest, OptimizeResult } from '../engine/optimize';

export type EngineProgress = (stage: 'horizon' | 'irradiance', done: number, total: number) => void;

export class EngineFailure extends Error {
  constructor(public code: string, message: string) { super(message); }
}

function call<T>(msg: unknown, onProgress?: (stage: any, done: number, total: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const w = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === 'progress') onProgress?.(m.stage, m.done, m.total);
      else if (m.type === 'done') { w.terminate(); resolve(m.out); }
      else if (m.type === 'error') { w.terminate(); reject(new EngineFailure(m.code, m.message)); }
    };
    w.onerror = (e) => { w.terminate(); reject(new EngineFailure('engine', e.message)); };
    w.postMessage(msg);
  });
}

/** Run the engine in a Web Worker (the input is copied, not transferred). */
export function runInWorker(input: EngineInput, onProgress?: EngineProgress): Promise<EngineOutput> {
  return call<EngineOutput>({ input }, onProgress);
}

/** Placement search for a planned house in a Web Worker. */
export function optimizeInWorker(req: OptimizeRequest, onProgress?: (done: number, total: number) => void): Promise<OptimizeResult> {
  return call<OptimizeResult>({ optimize: req }, (_s, d, t) => onProgress?.(d, t));
}
